import React, { useState, useEffect, useMemo } from 'react';
import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    Button,
    Grid,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Checkbox,
    FormControlLabel,
    Typography,
    Box,
    Alert,
    Tabs,
    Tab,
    Autocomplete,
    Chip,
    Stack,
    Divider,
    InputAdornment,
    IconButton,
    Collapse,
    Switch,
    Tooltip,
    CircularProgress
} from '@mui/material';
import {
    LocationOn,
    Security,
    Schedule,
    Info,
    Visibility,
    VisibilityOff,
    History as HistoryIcon
} from '@mui/icons-material';
import apiClient from '../api';
import debounce from 'lodash/debounce';
import { Paper, ListItem, ListItemText } from '@mui/material';
import { createDropLotLocation, updateDropLotLocation } from '../services/dropLotLocationService';
import { getAllCustomers } from '../services/customerService';
import { useAuth } from '../context/AuthContext';

// Time options for dropdowns
const timeOptions = [
    '12:00 AM', '12:30 AM', '1:00 AM', '1:30 AM', '2:00 AM', '2:30 AM', '3:00 AM', '3:30 AM',
    '4:00 AM', '4:30 AM', '5:00 AM', '5:30 AM', '6:00 AM', '6:30 AM', '7:00 AM', '7:30 AM',
    '8:00 AM', '8:30 AM', '9:00 AM', '9:30 AM', '10:00 AM', '10:30 AM', '11:00 AM', '11:30 AM',
    '12:00 PM', '12:30 PM', '1:00 PM', '1:30 PM', '2:00 PM', '2:30 PM', '3:00 PM', '3:30 PM',
    '4:00 PM', '4:30 PM', '5:00 PM', '5:30 PM', '6:00 PM', '6:30 PM', '7:00 PM', '7:30 PM',
    '8:00 PM', '8:30 PM', '9:00 PM', '9:30 PM', '10:00 PM', '10:30 PM', '11:00 PM', '11:30 PM'
];

const stateOptions = [
    { value: 'AL', label: 'Alabama' }, { value: 'AK', label: 'Alaska' }, { value: 'AZ', label: 'Arizona' },
    { value: 'AR', label: 'Arkansas' }, { value: 'CA', label: 'California' }, { value: 'CO', label: 'Colorado' },
    { value: 'CT', label: 'Connecticut' }, { value: 'DE', label: 'Delaware' }, { value: 'FL', label: 'Florida' },
    { value: 'GA', label: 'Georgia' }, { value: 'HI', label: 'Hawaii' }, { value: 'ID', label: 'Idaho' },
    { value: 'IL', label: 'Illinois' }, { value: 'IN', label: 'Indiana' }, { value: 'IA', label: 'Iowa' },
    { value: 'KS', label: 'Kansas' }, { value: 'KY', label: 'Kentucky' }, { value: 'LA', label: 'Louisiana' },
    { value: 'ME', label: 'Maine' }, { value: 'MD', label: 'Maryland' }, { value: 'MA', label: 'Massachusetts' },
    { value: 'MI', label: 'Michigan' }, { value: 'MN', label: 'Minnesota' }, { value: 'MS', label: 'Mississippi' },
    { value: 'MO', label: 'Missouri' }, { value: 'MT', label: 'Montana' }, { value: 'NE', label: 'Nebraska' },
    { value: 'NV', label: 'Nevada' }, { value: 'NH', label: 'New Hampshire' }, { value: 'NJ', label: 'New Jersey' },
    { value: 'NM', label: 'New Mexico' }, { value: 'NY', label: 'New York' }, { value: 'NC', label: 'North Carolina' },
    { value: 'ND', label: 'North Dakota' }, { value: 'OH', label: 'Ohio' }, { value: 'OK', label: 'Oklahoma' },
    { value: 'OR', label: 'Oregon' }, { value: 'PA', label: 'Pennsylvania' }, { value: 'RI', label: 'Rhode Island' },
    { value: 'SC', label: 'South Carolina' }, { value: 'SD', label: 'South Dakota' }, { value: 'TN', label: 'Tennessee' },
    { value: 'TX', label: 'Texas' }, { value: 'UT', label: 'Utah' }, { value: 'VT', label: 'Vermont' },
    { value: 'VA', label: 'Virginia' }, { value: 'WA', label: 'Washington' }, { value: 'WV', label: 'West Virginia' },
    { value: 'WI', label: 'Wisconsin' }, { value: 'WY', label: 'Wyoming' }
];

const amenityOptions = [
    'restrooms', 'parking', 'fuel', 'food', 'shower', 'laundry',
    'wifi', 'security_cameras', 'lighting', 'scales', 'maintenance'
];

const restrictionOptions = [
    'no_overnight_parking', 'no_idling', 'escort_required',
    'appointment_only', 'hazmat_restricted', 'size_restrictions'
];

const initialFormData = {
    customer: '',
    name: '',
    identifier: '',
    address: {
        street: '',
        city: '',
        state: '',
        zip: ''
    },
    gateCode: '',
    lotType: 'both',
    operatingHours: '24/7',
    openDays: {
        monday: true,
        tuesday: true,
        wednesday: true,
        thursday: true,
        friday: true,
        saturday: true,
        sunday: true
    },
    dayHours: {
        monday: { open: '', close: '' },
        tuesday: { open: '', close: '' },
        wednesday: { open: '', close: '' },
        thursday: { open: '', close: '' },
        friday: { open: '', close: '' },
        saturday: { open: '', close: '' },
        sunday: { open: '', close: '' }
    },
    capacity: {
        maxTrailers: '',
        maxTrucks: ''
    },
    amenities: [],
    restrictions: [],
    notes: '',
    driverInstructions: '',
    specialRequirements: '',
    contactInfo: {
        phone: '',
        email: '',
        emergencyContact: ''
    },
    priority: 'primary',
    accessRestrictions: {
        requiresAuthorization: true,
        authorizedRoles: ['driver']
    }
};

function DropLotLocationForm({ open, onClose, dropLot, customer, onSave }) {
    const { user } = useAuth();
    const [formData, setFormData] = useState(initialFormData);
    const [customers, setCustomers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState(0);
    const [showGateCode, setShowGateCode] = useState(false);
    const [useDetailedHours, setUseDetailedHours] = useState(false);
    // Address autocomplete state
    const [existingAddresses, setExistingAddresses] = useState([]);
    const [googleMapsAddresses, setGoogleMapsAddresses] = useState([]);
    const [addressesLoading, setAddressesLoading] = useState(false);
    const [streetInputValue, setStreetInputValue] = useState('');
    const [selectedFullAddress, setSelectedFullAddress] = useState(null);
    const [addressSearchType, setAddressSearchType] = useState('googlemaps'); // 'googlemaps' or 'existing'
    const [addressOpen, setAddressOpen] = useState(false);
    
    const isEditMode = Boolean(dropLot);

    useEffect(() => {
        if (open) {
            loadCustomers();
            if (dropLot) {
                setFormData({ ...initialFormData, ...dropLot });
                if (dropLot.dayHours && Object.values(dropLot.dayHours).some(day => day.open || day.close)) {
                    setUseDetailedHours(true);
                }
                if (dropLot.address?.street) setStreetInputValue(dropLot.address.street);
            } else if (customer) {
                setFormData(prev => ({ ...prev, customer: customer._id }));
            } else {
                setFormData(initialFormData);
                setStreetInputValue('');
            }
            setError(null);
        }
    }, [open, dropLot, customer]);

    // Fetch existing addresses when dialog opens
    useEffect(() => {
        const fetchExistingAddresses = async () => {
            if (!open) return;
            setAddressesLoading(true);
            try {
                const response = await apiClient.get('/customers/addresses');
                if (response.data && response.data.data) {
                    setExistingAddresses(response.data.data);
                }
            } catch (err) {
                console.error('Error fetching addresses:', err);
            } finally {
                setAddressesLoading(false);
            }
        };
        fetchExistingAddresses();
    }, [open]);

    // Debounced Google Maps address search
    const fetchGoogleMapsAddresses = useMemo(() => debounce(async (query) => {
        if (!query || query.length < 3) {
            setAddressesLoading(false);
            return;
        }
        setAddressesLoading(true);
        try {
            const response = await apiClient.get('/customers/googlemaps-search', { params: { query } });
            if (response.data && response.data.data) {
                setGoogleMapsAddresses(response.data.data);
            }
        } catch (err) {
            if (err?.response?.status !== 429) console.error('Error fetching Google Maps addresses:', err);
        } finally {
            setAddressesLoading(false);
        }
    }, 600), []);

    useEffect(() => {
        if (!open) return;
        if (addressSearchType === 'googlemaps' && streetInputValue.length >= 3) {
            fetchGoogleMapsAddresses(streetInputValue);
        }
    }, [streetInputValue, addressSearchType, fetchGoogleMapsAddresses, open]);

    const getAddressSuggestions = () => {
        if (addressSearchType === 'googlemaps') return googleMapsAddresses;
        return existingAddresses.map(address => ({
            ...address,
            label: `${address.street}, ${address.city}, ${address.state} ${address.zip}`
        }));
    };

    const handleToggleAddressSearch = () => {
        setAddressSearchType(prev => prev === 'googlemaps' ? 'existing' : 'googlemaps');
        setGoogleMapsAddresses([]);
    };

    const handleStreetInputChange = (event, newInputValue) => {
        setStreetInputValue(newInputValue);
        if (selectedFullAddress && newInputValue !== selectedFullAddress.street) {
            setSelectedFullAddress(null);
        }
        setFormData(prev => ({
            ...prev,
            address: { ...prev.address, street: newInputValue }
        }));
    };

    const handleAddressSelect = (event, newValue) => {
        if (!newValue) return;
        setSelectedFullAddress(newValue);
        let stateValue = newValue.state || '';
        let zipValue = newValue.zip || '';
        if (!zipValue && stateValue) {
            const zipInStateMatch = stateValue.match(/\b(\d{5}(-\d{4})?)\b/);
            if (zipInStateMatch) {
                zipValue = zipInStateMatch[1];
                stateValue = stateValue.replace(zipValue, '').trim();
            }
        }
        if (stateValue.length > 2) {
            const option = stateOptions.find(o => o.label.toLowerCase() === stateValue.toLowerCase());
            if (option) stateValue = option.value;
        }
        if (zipValue.includes('-')) zipValue = zipValue.split('-')[0];
        setFormData(prev => ({
            ...prev,
            address: {
                ...prev.address,
                street: newValue.street || '',
                city: newValue.city || '',
                state: stateValue,
                zip: zipValue,
                coordinates: newValue.coordinates || prev.address.coordinates
            }
        }));
    };

    const renderAddressOption = (props, option) => (
        <ListItem {...props} divider>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', width: '100%' }}>
                <Box sx={{ mr: 2, color: 'text.secondary', mt: 0.5 }}>
                    {addressSearchType === 'googlemaps' ? (
                        <LocationOn color="primary" fontSize="small" />
                    ) : (
                        <HistoryIcon color="action" fontSize="small" />
                    )}
                </Box>
                <ListItemText
                    primary={<Typography variant="body1" noWrap>{option.street}</Typography>}
                    secondary={<Typography variant="body2" color="text.secondary" noWrap>{option.city}, {option.state} {option.zip}</Typography>}
                />
            </Box>
        </ListItem>
    );

    const renderAddressInput = (params) => (
        <TextField
            {...params}
            margin="dense"
            label="Street Address"
            placeholder={addressSearchType === 'googlemaps' ? 'Type to search US addresses' : 'Select from existing addresses'}
            fullWidth
            variant="outlined"
            InputProps={{
                ...params.InputProps,
                startAdornment: (
                    <InputAdornment position="start">
                        <Tooltip title={addressSearchType === 'googlemaps' ? 'Searching US addresses' : 'Showing existing addresses'}>
                            <IconButton onClick={handleToggleAddressSearch} size="small" color={addressSearchType === 'googlemaps' ? 'primary' : 'default'}>
                                {addressSearchType === 'googlemaps' ? <LocationOn /> : <HistoryIcon />}
                            </IconButton>
                        </Tooltip>
                    </InputAdornment>
                ),
                endAdornment: (
                    <>
                        {addressesLoading ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                    </>
                )
            }}
        />
    );

    const loadCustomers = async () => {
        try {
            const response = await getAllCustomers();
            setCustomers(response.data || []);
        } catch (err) {
            console.error('Error loading customers:', err);
        }
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        
        if (name.includes('.')) {
            const [parent, child] = name.split('.');
            setFormData(prev => ({
                ...prev,
                [parent]: {
                    ...prev[parent],
                    [child]: type === 'checkbox' ? checked : value
                }
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: type === 'checkbox' ? checked : value
            }));
        }
    };

    const handleNestedChange = (parent, child, value) => {
        setFormData(prev => ({
            ...prev,
            [parent]: {
                ...prev[parent],
                [child]: value
            }
        }));
    };

    const handleArrayChange = (field, values) => {
        setFormData(prev => ({
            ...prev,
            [field]: values
        }));
    };

    const handleDayToggle = (day) => {
        setFormData(prev => ({
            ...prev,
            openDays: {
                ...prev.openDays,
                [day]: !prev.openDays[day]
            }
        }));
    };

    const handleTimeChange = (day, timeType, value) => {
        setFormData(prev => ({
            ...prev,
            dayHours: {
                ...prev.dayHours,
                [day]: {
                    ...prev.dayHours[day],
                    [timeType]: value
                }
            }
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        try {
            let response;
            if (isEditMode) {
                response = await updateDropLotLocation(dropLot._id, formData);
            } else {
                response = await createDropLotLocation(formData);
            }

            if (onSave) {
                onSave(response.data);
            }
            onClose();
        } catch (err) {
            console.error('Error saving drop lot location:', err);
            setError(err.response?.data?.message || 'An error occurred while saving');
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        if (!loading) {
            onClose();
        }
    };

    const TabPanel = ({ children, value, index }) => (
        <div hidden={value !== index}>
            {value === index && <Box sx={{ py: 2 }}>{children}</Box>}
        </div>
    );

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth scroll="paper">
            <DialogTitle>
                <Stack direction="row" alignItems="center" spacing={1}>
                    <LocationOn color="primary" />
                    <Typography variant="h6">
                        {isEditMode ? 'Edit Drop Lot Location' : 'Add New Drop Lot Location'}
                    </Typography>
                </Stack>
            </DialogTitle>
            
            <form onSubmit={handleSubmit}>
                <DialogContent sx={{ maxHeight: 'calc(100vh - 220px)', overflowY: 'auto', pb: 8 }}>
                    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                    
                    <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
                        <Tab label="Basic Info" icon={<Info />} />
                        <Tab label="Address" icon={<LocationOn />} />
                        <Tab label="Security" icon={<Security />} />
                        <Tab label="Operations" icon={<Schedule />} />
                    </Tabs>

                    {/* Basic Information Tab */}
                    <TabPanel value={activeTab} index={0}>
                        <Grid container spacing={3}>
                            <Grid item xs={12}>
                                <FormControl fullWidth required>
                                    <InputLabel>Customer</InputLabel>
                                    <Select
                                        name="customer"
                                        value={formData.customer}
                                        onChange={handleChange}
                                        disabled={Boolean(customer)}
                                    >
                                        {customers.map((cust) => (
                                            <MenuItem key={cust._id} value={cust._id}>
                                                {cust.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            
                            <Grid item xs={12} md={8}>
                                <TextField
                                    fullWidth
                                    required
                                    name="name"
                                    label="Drop Lot Name"
                                    value={formData.name}
                                    onChange={handleChange}
                                    placeholder="e.g., West Gate Lot"
                                />
                            </Grid>
                            
                            <Grid item xs={12} md={4}>
                                <TextField
                                    fullWidth
                                    name="identifier"
                                    label="Identifier/Code"
                                    value={formData.identifier}
                                    onChange={handleChange}
                                    placeholder="e.g., WG-01"
                                />
                            </Grid>
                            
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth>
                                    <InputLabel>Lot Type</InputLabel>
                                    <Select
                                        name="lotType"
                                        value={formData.lotType}
                                        onChange={handleChange}
                                    >
                                        <MenuItem value="drop_only">Drop Only</MenuItem>
                                        <MenuItem value="pickup_only">Pickup Only</MenuItem>
                                        <MenuItem value="both">Both Drop & Pickup</MenuItem>
                                        <MenuItem value="staging">Staging Area</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth>
                                    <InputLabel>Priority</InputLabel>
                                    <Select
                                        name="priority"
                                        value={formData.priority}
                                        onChange={handleChange}
                                    >
                                        <MenuItem value="primary">Primary</MenuItem>
                                        <MenuItem value="secondary">Secondary</MenuItem>
                                        <MenuItem value="backup">Backup</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    multiline
                                    rows={3}
                                    name="notes"
                                    label="Notes"
                                    value={formData.notes}
                                    onChange={handleChange}
                                    placeholder="General notes about this drop lot location..."
                                />
                            </Grid>
                        </Grid>
                    </TabPanel>

                    {/* Address Tab */}
                    <TabPanel value={activeTab} index={1}>
                        <Grid container spacing={3}>
                            <Grid item xs={12}>
                                <Autocomplete
                                    freeSolo
                                    options={getAddressSuggestions()}
                                    getOptionLabel={(option) => typeof option === 'string' ? option : option.label || ''}
                                    inputValue={streetInputValue}
                                    onInputChange={handleStreetInputChange}
                                    onChange={handleAddressSelect}
                                    loading={addressesLoading}
                                    open={addressOpen}
                                    onOpen={() => setAddressOpen(true)}
                                    onClose={() => setAddressOpen(false)}
                                    openOnFocus
                                    filterOptions={(x) => x}
                                    loadingText="Searching addresses..."
                                    noOptionsText={
                                        addressSearchType === 'googlemaps' 
                                            ? streetInputValue.length < 3 
                                                ? 'Type at least 3 characters to search' 
                                                : 'No addresses found'
                                            : 'No existing addresses found'
                                    }
                                    renderInput={renderAddressInput}
                                    renderOption={renderAddressOption}
                                    PaperComponent={(props) => (
                                        <Paper {...props} elevation={4} sx={{ mt: 0.5 }} />
                                    )}
                                />
                            </Grid>

                            <Grid item xs={12} md={5}>
                                <TextField
                                    fullWidth
                                    required
                                    name="address.city"
                                    label="City"
                                    value={formData.address.city}
                                    onChange={(e) => handleNestedChange('address', 'city', e.target.value)}
                                />
                            </Grid>
                            
                            <Grid item xs={12} md={3}>
                                <Select
                                    fullWidth
                                    value={formData.address.state}
                                    onChange={(e) => handleNestedChange('address', 'state', e.target.value)}
                                    displayEmpty
                                    inputProps={{ 'aria-label': 'State' }}
                                >
                                    <MenuItem value=""><em>Select State</em></MenuItem>
                                    {stateOptions.map(option => (
                                        <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                                    ))}
                                </Select>
                            </Grid>
                            
                            <Grid item xs={12} md={4}>
                                <TextField
                                    fullWidth
                                    required
                                    name="address.zip"
                                    label="ZIP Code"
                                    value={formData.address.zip}
                                    onChange={(e) => handleNestedChange('address', 'zip', e.target.value)}
                                />
                            </Grid>
                            
                            <Grid item xs={12}>
                                <Divider sx={{ my: 2 }} />
                                <Typography variant="h6" gutterBottom>Capacity & Amenities</Typography>
                            </Grid>
                            
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    type="number"
                                    name="capacity.maxTrailers"
                                    label="Max Trailers"
                                    value={formData.capacity.maxTrailers}
                                    onChange={handleChange}
                                    inputProps={{ min: 0 }}
                                />
                            </Grid>
                            
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    type="number"
                                    name="capacity.maxTrucks"
                                    label="Max Trucks"
                                    value={formData.capacity.maxTrucks}
                                    onChange={handleChange}
                                    inputProps={{ min: 0 }}
                                />
                            </Grid>
                            
                            <Grid item xs={12} md={6}>
                                <Autocomplete
                                    multiple
                                    options={amenityOptions}
                                    value={formData.amenities}
                                    onChange={(e, newValue) => handleArrayChange('amenities', newValue)}
                                    renderTags={(value, getTagProps) =>
                                        value.map((option, index) => (
                                            <Chip key={option} label={option.replace('_', ' ')} {...getTagProps({ index })} />
                                        ))
                                    }
                                    renderInput={(params) => (
                                        <TextField {...params} label="Amenities" placeholder="Select amenities..." />
                                    )}
                                />
                            </Grid>
                            
                            <Grid item xs={12} md={6}>
                                <Autocomplete
                                    multiple
                                    options={restrictionOptions}
                                    value={formData.restrictions}
                                    onChange={(e, newValue) => handleArrayChange('restrictions', newValue)}
                                    renderTags={(value, getTagProps) =>
                                        value.map((option, index) => (
                                            <Chip key={option} label={option.replace('_', ' ')} color="warning" {...getTagProps({ index })} />
                                        ))
                                    }
                                    renderInput={(params) => (
                                        <TextField {...params} label="Restrictions" placeholder="Select restrictions..." />
                                    )}
                                />
                            </Grid>
                        </Grid>
                    </TabPanel>

                    {/* Security Tab */}
                    <TabPanel value={activeTab} index={2}>
                        <Grid container spacing={3}>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    name="gateCode"
                                    label="Gate Code"
                                    type={showGateCode ? 'text' : 'password'}
                                    value={formData.gateCode}
                                    onChange={handleChange}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    onClick={() => setShowGateCode(!showGateCode)}
                                                    edge="end"
                                                >
                                                    {showGateCode ? <VisibilityOff /> : <Visibility />}
                                                </IconButton>
                                            </InputAdornment>
                                        )
                                    }}
                                    helperText="Gate code will be encrypted and only visible to authorized users"
                                />
                            </Grid>
                            
                            <Grid item xs={12}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={formData.accessRestrictions.requiresAuthorization}
                                            onChange={(e) => handleNestedChange('accessRestrictions', 'requiresAuthorization', e.target.checked)}
                                        />
                                    }
                                    label="Require Authorization for Gate Code Access"
                                />
                            </Grid>
                            
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    multiline
                                    rows={2}
                                    name="driverInstructions"
                                    label="Driver Instructions"
                                    value={formData.driverInstructions}
                                    onChange={handleChange}
                                    placeholder="Special instructions for drivers..."
                                />
                            </Grid>
                            
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    multiline
                                    rows={2}
                                    name="specialRequirements"
                                    label="Special Requirements"
                                    value={formData.specialRequirements}
                                    onChange={handleChange}
                                    placeholder="Any special requirements or procedures..."
                                />
                            </Grid>
                        </Grid>
                    </TabPanel>

                    {/* Operations Tab */}
                    <TabPanel value={activeTab} index={3}>
                        <Grid container spacing={3}>
                            <Grid item xs={12}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={useDetailedHours}
                                            onChange={(e) => setUseDetailedHours(e.target.checked)}
                                        />
                                    }
                                    label="Use Detailed Operating Hours"
                                />
                            </Grid>
                            
                            <Grid item xs={12}>
                                <Collapse in={!useDetailedHours}>
                                    <TextField
                                        fullWidth
                                        name="operatingHours"
                                        label="Operating Hours"
                                        value={formData.operatingHours}
                                        onChange={handleChange}
                                        placeholder="e.g., 24/7, Monday-Friday 8AM-5PM"
                                    />
                                </Collapse>
                                
                                <Collapse in={useDetailedHours}>
                                    <Box>
                                        <Typography variant="subtitle1" gutterBottom>Weekly Schedule</Typography>
                                        {Object.keys(formData.openDays).map((day) => (
                                            <Box key={day} sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                                                <Stack direction="row" alignItems="center" spacing={2}>
                                                    <FormControlLabel
                                                        control={
                                                            <Checkbox
                                                                checked={formData.openDays[day]}
                                                                onChange={() => handleDayToggle(day)}
                                                            />
                                                        }
                                                        label={day.charAt(0).toUpperCase() + day.slice(1)}
                                                        sx={{ minWidth: 120 }}
                                                    />
                                                    
                                                    {formData.openDays[day] && (
                                                        <>
                                                            <Autocomplete
                                                                options={timeOptions}
                                                                value={formData.dayHours[day].open}
                                                                onChange={(e, newValue) => handleTimeChange(day, 'open', newValue || '')}
                                                                renderInput={(params) => (
                                                                    <TextField {...params} label="Open" size="small" sx={{ width: 120 }} />
                                                                )}
                                                            />
                                                            <Typography>to</Typography>
                                                            <Autocomplete
                                                                options={timeOptions}
                                                                value={formData.dayHours[day].close}
                                                                onChange={(e, newValue) => handleTimeChange(day, 'close', newValue || '')}
                                                                renderInput={(params) => (
                                                                    <TextField {...params} label="Close" size="small" sx={{ width: 120 }} />
                                                                )}
                                                            />
                                                        </>
                                                    )}
                                                </Stack>
                                            </Box>
                                        ))}
                                    </Box>
                                </Collapse>
                            </Grid>
                            
                            <Grid item xs={12}>
                                <Divider sx={{ my: 2 }} />
                                <Typography variant="h6" gutterBottom>Contact Information</Typography>
                            </Grid>
                            
                            <Grid item xs={12} md={4}>
                                <TextField
                                    fullWidth
                                    name="contactInfo.phone"
                                    label="Phone"
                                    value={formData.contactInfo.phone}
                                    onChange={handleChange}
                                />
                            </Grid>
                            
                            <Grid item xs={12} md={4}>
                                <TextField
                                    fullWidth
                                    name="contactInfo.email"
                                    label="Email"
                                    type="email"
                                    value={formData.contactInfo.email}
                                    onChange={handleChange}
                                />
                            </Grid>
                            
                            <Grid item xs={12} md={4}>
                                <TextField
                                    fullWidth
                                    name="contactInfo.emergencyContact"
                                    label="Emergency Contact"
                                    value={formData.contactInfo.emergencyContact}
                                    onChange={handleChange}
                                />
                            </Grid>
                        </Grid>
                    </TabPanel>
                </DialogContent>
                
                <DialogActions sx={{ p: 3, position: 'sticky', bottom: 0, bgcolor: 'background.paper' }}>
                    <Button onClick={handleClose} disabled={loading}>
                        Cancel
                    </Button>
                    <Button 
                        type="submit" 
                        variant="contained" 
                        disabled={loading}
                        startIcon={loading ? null : <LocationOn />}
                    >
                        {loading ? 'Saving...' : isEditMode ? 'Update Drop Lot' : 'Create Drop Lot'}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
}

export default DropLotLocationForm; 